import { useRouter } from 'next/router';
import { useMemo } from 'react';

import type { ReviewsKind } from '@/domains/productDiscovery/ArticleReviews/modules/allReviews/AllReviews';
import { useDetail } from '@/domains/productDiscovery/ProductPage/hooks/useDetail';

export const { reviews: reviewsTab, description: descriptionTab } = {
  reviews: {
    mainHash: 'tab-reviews',
    secondaryHash: 'tab-professional-reviews',
  },
  description: {
    mainHash: 'tab-description',
    secondaryHash: 'tab-description-attributes',
    tertiaryHash: 'tab-description-sustainability-attributes',
  },
};

interface Tab {
  display: boolean;
  hash: string;
  matches: boolean;
}

interface TabWithParams<Params> extends Tab {
  params: Params;
}

interface Return {
  tabs: {
    description: TabWithParams<{
      descriptionAttributesHash: string;
      sustainabilityAttributesHash: string;
    }>;
    reviews: Tab;
  };
  isTabsContainerDisplayed: boolean;
  shouldHideTabs: boolean;
  hash: string;
  closeTabsContainer: () => void;
  goToDescription: () => void;
  goToDescriptionAttributes: () => void;
  goToDescriptionSustainabilityAttributes: () => void;
  goToReviews: (kind?: ReviewsKind) => void;
}

export const useTabs = (): Return => {
  const { asPath, push } = useRouter();
  const [uriWithoutHash, hash] = asPath.split('#');
  const { totalRating, description } = useDetail<'b2c'>();

  const tabs: Return['tabs'] = useMemo(
    () => ({
      reviews: {
        hash: reviewsTab.mainHash,
        display: !!totalRating,
        matches:
          hash === reviewsTab.mainHash || hash === reviewsTab.secondaryHash,
      },
      description: {
        hash: descriptionTab.mainHash,
        display: !!description,
        matches:
          hash === descriptionTab.mainHash ||
          hash === descriptionTab.secondaryHash ||
          hash === descriptionTab.tertiaryHash,
        params: {
          descriptionAttributesHash: descriptionTab.secondaryHash,
          sustainabilityAttributesHash: descriptionTab.tertiaryHash,
        },
      },
    }),
    [hash, totalRating, description],
  );

  const shouldHideTabs = useMemo(
    () => Object.values(tabs).filter((t) => t.display).length === 1,
    [tabs],
  );
  const isTabsContainerDisplayed = hash?.includes('tab-');

  const closeTabsContainer = () =>
    push(uriWithoutHash, undefined, { shallow: true, scroll: false });

  const goToReviews = (kind: ReviewsKind = 'all') => {
    push(`#${reviewsTab.mainHash}`, undefined, {
      shallow: true,
      scroll: false,
    });
  };

  const goToDescription = () =>
    push(`#${tabs.description.hash}`, undefined, {
      shallow: true,
      scroll: false,
    });

  const goToDescriptionAttributes = () =>
    push(`#${descriptionTab.secondaryHash}`, undefined, {
      shallow: true,
      scroll: false,
    });

  const goToDescriptionSustainabilityAttributes = () =>
    push(`#${descriptionTab.tertiaryHash}`, undefined, {
      shallow: true,
      scroll: false,
    });

  return {
    tabs,
    isTabsContainerDisplayed,
    shouldHideTabs,
    hash,
    closeTabsContainer,
    goToDescription,
    goToReviews,
    goToDescriptionAttributes,
    goToDescriptionSustainabilityAttributes,
  };
};
